// WhatsApp Authentication Hook - OTP via WhatsApp Cloud API
// Date: 2026-03-04
// Purpose: Handle WhatsApp registration and authentication via OTP sent through
// the WhatsApp Cloud API (template: tyrelinkverify). No redirect to WhatsApp.

import { useState } from 'react';
import { supabase } from '../services/supabase';

interface WhatsAppRegistrationData {
  whatsappNumber: string;
  fullName: string;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: number;
  role: 'customer' | 'business';
  marketingConsent?: boolean;
}

interface WhatsAppAuthState {
  loading: boolean;
  error: string | null;
  otpSent: boolean;
  registrationInProgress: boolean;
  verified: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Helper: call a Supabase Edge Function
 */
async function callEdgeFunction(functionName: string, body: Record<string, unknown>): Promise<{ ok: boolean; data: any }> {
  const url = `${SUPABASE_URL}/functions/v1/${functionName}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
    },
    body: JSON.stringify(body)
  });
  const data = await response.json();
  return { ok: response.ok, data };
}

/**
 * Normalize a phone number to canonical digits-only international format.
 * All of these become 27830009999:
 *   083 000 9999  |  0830009999  |  +27 83 000 9999  |  27830009999  |  0027830009999
 * Non-SA international numbers (e.g. 91...) pass through unchanged apart from
 * cleanup, so existing foreign test users keep working.
 * Throws a user-friendly Error if the input can't be a valid phone number —
 * every caller is already wrapped in try/catch that surfaces error.message.
 */
function formatPhone(phone: string): string {
  const invalidMsg =
    'Please enter a valid phone number, e.g. 083 123 4567 or +27 83 123 4567';

  if (!phone) throw new Error(invalidMsg);

  // Strip spaces, dashes, brackets, dots
  let cleaned = phone.trim().replace(/[\s\-().]/g, '');

  // Strip international prefixes: '+27...' or '0027...'
  if (cleaned.startsWith('+')) cleaned = cleaned.slice(1);
  else if (cleaned.startsWith('00')) cleaned = cleaned.slice(2);

  // Anything non-numeric left means it was never a phone number
  if (!/^\d+$/.test(cleaned)) throw new Error(invalidMsg);

  // South African local format: leading 0 -> 27
  if (cleaned.startsWith('0')) cleaned = '27' + cleaned.slice(1);

  // Validate length: SA numbers are 27 + 9 digits = 11;
  // other international numbers 10-15 digits (E.164 bounds)
  if (cleaned.startsWith('27')) {
    if (cleaned.length !== 11) throw new Error(invalidMsg);
  } else if (cleaned.length < 10 || cleaned.length > 15) {
    throw new Error(invalidMsg);
  }

  return cleaned;
}

/** Exported so UI components can validate/normalize before starting a flow */
export const normalizePhoneNumber = formatPhone;

export function useWhatsAppAuth() {
  const [state, setState] = useState<WhatsAppAuthState>({
    loading: false,
    error: null,
    otpSent: false,
    registrationInProgress: false,
    verified: false
  });

  // ──────── Send OTP ────────

  /**
   * Send a 6-digit OTP to the given WhatsApp number via the Cloud API
   * (uses the "tyrelinkverify" template).
   * @param purpose 'login' | 'register'
   */
  const sendOTP = async (phoneNumber: string, purpose: 'login' | 'register'): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null, otpSent: false }));

    try {
      const formatted = formatPhone(phoneNumber);
      const { ok, data } = await callEdgeFunction('send-whatsapp-otp', {
        phone_number: formatted,
        purpose
      });

      if (!ok) {
        throw new Error(data?.error || 'Failed to send OTP');
      }

      setState(prev => ({ ...prev, loading: false, otpSent: true, error: null }));
      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to send OTP'
      }));
      return false;
    }
  };

  // ──────── Verify OTP ────────

  /**
   * Verify the 6-digit OTP code the user entered
   */
  const verifyOTP = async (phoneNumber: string, otpCode: string): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formatted = formatPhone(phoneNumber);
      const { ok, data } = await callEdgeFunction('verify-whatsapp-otp', {
        phone_number: formatted,
        otp_code: otpCode
      });

      if (!ok || !data?.valid) {
        throw new Error(data?.error || 'Invalid or expired OTP code');
      }

      setState(prev => ({ ...prev, loading: false, verified: true, error: null }));
      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'OTP verification failed'
      }));
      return false;
    }
  };

  // ──────── Register via WhatsApp OTP ────────

  /**
   * Step 1: Send OTP for registration (checks if number is already registered first).
   */
  const sendRegistrationOTP = async (registrationData: WhatsAppRegistrationData): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formatted = formatPhone(registrationData.whatsappNumber);

      // Check if already registered
      const { data: existingUsers, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, whatsapp_number, whatsapp_verified')
        .eq('whatsapp_number', formatted);

      if (checkError) throw checkError;

      if (existingUsers && existingUsers.length > 0) {
        const user = existingUsers[0] as any;
        if (user.whatsapp_verified) {
          throw new Error('This WhatsApp number is already registered. Please sign in instead.');
        }
      }

      // Store registration data for after OTP verification
      const registrationKey = `whatsapp_registration_${formatted.replace(/\D/g, '')}`;
      sessionStorage.setItem(registrationKey, JSON.stringify(registrationData));

      // Send OTP
      const sent = await sendOTP(formatted, 'register');
      if (!sent) return false;

      setState(prev => ({
        ...prev,
        loading: false,
        otpSent: true,
        registrationInProgress: true,
        error: null
      }));
      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to send registration OTP'
      }));
      return false;
    }
  };

  /**
   * Step 2: Verify OTP and complete registration
   */
  const verifyRegistrationOTP = async (phoneNumber: string, otpCode: string, registrationData: WhatsAppRegistrationData): Promise<{ success: boolean; error?: string }> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formatted = formatPhone(phoneNumber);

      // Verify OTP first
      console.log('[WA Auth] Verifying registration OTP for:', formatted);
      const valid = await verifyOTP(formatted, otpCode);
      if (!valid) {
        const errMsg = 'Invalid or expired OTP code';
        return { success: false, error: errMsg };
      }
      console.log('[WA Auth] OTP verified, creating auth account...');

      // Create user profile in DB
      // First, create a deterministic auth account
      const email = `${formatted.replace('+', '')}@whatsapp.tyrelink.co.za`;
      // Generate a secure-ish password based on phone + random
      const password = `WA_OTP_${formatted.replace(/\D/g, '')}_${crypto.randomUUID().slice(0, 8)}`;

      // Store password temporarily so completeAuth can use it
      sessionStorage.setItem(`wa_pass_${formatted.replace(/\D/g, '')}`, password);

      console.log('[WA Auth] Signing up with email:', email);
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            whatsapp_number: formatted,
            full_name: registrationData.fullName,
            registration_method: 'whatsapp_otp',
            vehicle_make: registrationData.vehicleMake,
            vehicle_model: registrationData.vehicleModel,
            vehicle_year: registrationData.vehicleYear
          }
        }
      });

      if (signUpError) {
        console.error('[WA Auth] SignUp error:', signUpError);
        throw new Error(signUpError.message || 'Failed to create account');
      }
      console.log('[WA Auth] SignUp success, user ID:', signUpData.user?.id);

      // Mark user as confirmed via edge function
      if (signUpData.user?.id) {
        try {
          console.log('[WA Auth] Marking user confirmed...');
          await callEdgeFunction('mark-user-confirmed', { user_id: signUpData.user.id });
        } catch (confirmError) {
          console.warn('[WA Auth] User confirmation edge function failed:', confirmError);
        }
      }

      // Update/create user_profiles entry
      if (signUpData.user?.id) {
        console.log('[WA Auth] Upserting user_profiles...');
        const { error: upsertError } = await supabase.from('user_profiles').upsert({
          id: signUpData.user.id,
          role: registrationData.role || 'customer',
          full_name: registrationData.fullName,
          phone: formatted,
          whatsapp_number: formatted,
          whatsapp_verified: true,
          vehicle_make: registrationData.vehicleMake,
          vehicle_model: registrationData.vehicleModel,
          vehicle_year: registrationData.vehicleYear,
          marketing_consent: registrationData.marketingConsent ?? null,
          marketing_consent_at: registrationData.marketingConsent !== undefined ? new Date().toISOString() : null
        }, { onConflict: 'id' });
        if (upsertError) console.warn('[WA Auth] user_profiles upsert error:', upsertError);
      }

      // Sign in immediately
      console.log('[WA Auth] Signing in with password...');
      const signInResult = await supabase.auth.signInWithPassword({ email, password });
      if (signInResult.error) {
        console.error('[WA Auth] SignIn error:', signInResult.error);
        throw new Error('Account created but auto sign-in failed. Please sign in manually.');
      }
      console.log('[WA Auth] Registration + sign-in complete!');

      // Fire-and-forget: send the competition welcome + referral share message
      // via WhatsApp (never into the web chat). Failures here must never break
      // registration — the user is already signed in at this point.
      fetch('/api/whatsapp/referral-welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formatted })
      }).catch(err => console.warn('[WA Auth] referral welcome send failed:', err));

      setState(prev => ({
        ...prev,
        loading: false,
        verified: true,
        registrationInProgress: false,
        error: null
      }));
      return { success: true };
    } catch (error: any) {
      const errMsg = error.message || 'Registration failed';
      console.error('[WA Auth] verifyRegistrationOTP error:', errMsg);
      setState(prev => ({
        ...prev,
        loading: false,
        error: errMsg
      }));
      return { success: false, error: errMsg };
    }
  };

  // ──────── Login via WhatsApp OTP ────────

  /**
   * Step 1: Check user exists and send OTP for login
   */
  const sendLoginOTP = async (phoneNumber: string): Promise<boolean> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formatted = formatPhone(phoneNumber);

      // Check if user exists
      const { data: users, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, full_name, whatsapp_number, whatsapp_verified')
        .eq('whatsapp_number', formatted);

      if (checkError) throw checkError;

      if (!users || users.length === 0) {
        throw new Error('WhatsApp number not found. Please register first.');
      }

      // Send OTP
      const sent = await sendOTP(formatted, 'login');
      return sent;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to send login OTP'
      }));
      return false;
    }
  };

  /**
   * Step 2: Verify OTP and complete login authentication
   */
  const verifyLoginOTP = async (phoneNumber: string, otpCode: string): Promise<{ success: boolean; error?: string }> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formatted = formatPhone(phoneNumber);

      // Verify OTP — on success for 'login', the edge function rotates the
      // account password server-side and returns it so we can sign in.
      console.log('[WA Auth] Verifying login OTP for:', formatted);
      const { ok, data } = await callEdgeFunction('verify-whatsapp-otp', {
        phone_number: formatted,
        otp_code: otpCode
      });

      if (!ok || !data?.valid) {
        const errMsg = data?.error || 'Invalid or expired OTP code';
        setState(prev => ({ ...prev, loading: false, error: errMsg }));
        return { success: false, error: errMsg };
      }
      console.log('[WA Auth] OTP verified, completing login...');

      const email = `${formatted.replace('+', '')}@whatsapp.tyrelink.co.za`;
      const digitsKey = formatted.replace(/\D/g, '');

      // Preferred: the fresh password issued by the edge function
      let password: string | null = data.temp_password || null;

      // Fallback: same-session password from a registration moments ago
      if (!password) {
        password = sessionStorage.getItem(`wa_pass_${digitsKey}`);
      }

      if (!password) {
        throw new Error('Could not complete sign-in. Please try again.');
      }

      const signInResult = await supabase.auth.signInWithPassword({ email, password });
      if (signInResult.error) {
        console.error('[WA Auth] signIn failed after OTP:', signInResult.error);
        throw new Error('Authentication failed after OTP verification. Please try again.');
      }

      // Cache for this session so an immediate re-login is seamless
      sessionStorage.setItem(`wa_pass_${digitsKey}`, password);

      console.log('[WA Auth] Login complete!');
      setState(prev => ({ ...prev, loading: false, verified: true, error: null }));
      return { success: true };
    } catch (error: any) {
      const errMsg = error.message || 'Login verification failed';
      console.error('[WA Auth] verifyLoginOTP error:', errMsg);
      setState(prev => ({
        ...prev,
        loading: false,
        error: errMsg
      }));
      return { success: false, error: errMsg };
    }
  };

  // ──────── Check User Exists ────────

  const checkUserExists = async (phoneNumber: string): Promise<boolean> => {
    try {
      const formatted = formatPhone(phoneNumber);
      const { data: users, error } = await supabase
        .from('user_profiles')
        .select('id, phone, whatsapp_verified')
        .or(`whatsapp_number.eq.${formatted},phone.eq.${formatted}`);
      
      console.log(users)

      if (error) {
        console.error('Error checking user existence:', error);
        return false;
      }

      return !!(users && users.length > 0);
    } catch (error) {
      console.error('Error checking user existence:', error);
      return false;
    }
  };

  // ──────── Reset / Config ────────

  const resetState = () => {
    setState({
      loading: false,
      error: null,
      otpSent: false,
      registrationInProgress: false,
      verified: false
    });
  };

  const isConfigured = (): boolean => {
    return !!(SUPABASE_URL && SUPABASE_ANON_KEY);
  };

  return {
    ...state,
    // OTP-based methods
    sendOTP,
    verifyOTP,
    sendRegistrationOTP,
    verifyRegistrationOTP,
    sendLoginOTP,
    verifyLoginOTP,
    checkUserExists,
    resetState,
    isConfigured,

    // Legacy compat aliases (map old names to new methods)
    registerViaWhatsApp: sendRegistrationOTP,
    signInViaWhatsApp: sendLoginOTP,
    completeAuthenticationAfterWhatsApp: (phoneNumber: string) => {
      console.warn('completeAuthenticationAfterWhatsApp is deprecated. Use verifyLoginOTP instead.');
      return Promise.resolve(false);
    },
    pollForRegistrationStatus: (_phoneNumber: string) => {
      console.warn('pollForRegistrationStatus is deprecated. Use verifyRegistrationOTP instead.');
      return Promise.resolve(false);
    },
    sendWhatsAppOTP: sendOTP,
    loginWithWhatsApp: sendLoginOTP
  };
}

export type { WhatsAppRegistrationData };
